import {
  DAMAGE_REFLECTION_JEWEL_ID,
  type JewelSocketProfile,
  toExactBigInt,
} from '@kenocyber/shared';
import type { JewelEmittedEffect } from './origin';
import { PROC_CHANCE_DENOMINATOR, PROC_LEVEL_CAP, procChanceNumerator } from './proc-lottery';

/**
 * **A Égide Espinhosa: uma rolagem DEFENSIVA, e um payload que muda de dono.**
 *
 * ## O que a separa das cinco joias que já existem
 *
 * As cinco sorteiam do lado de quem ATACA, e o que decidem é o que o golpe
 * passa a ser: o Ciclone acrescenta um corte, a Bomba substitui um projétil, o
 * Míssil nasce ao lado da salva, o Veneno e o Sangramento aplicam estado. Todas
 * perguntam "o que é que este disparo produz".
 *
 * Esta pergunta é outra, e é feita do outro lado: **"este efeito hostil chega
 * mesmo a acontecer-me?"** A rolagem é do DEFENSOR, o instante é aquele em que
 * o efeito ia resolver-se contra a nave, e o resultado não é um golpe novo - é
 * o mesmo payload, com o mesmo dano congelado e os mesmos parâmetros, apontado
 * ao contrário.
 *
 * É por isso que ela tem fluxo próprio, tabela própria e ficheiro próprio: nada
 * do que ela decide passa pela loteria ofensiva, e meter uma rolagem defensiva
 * nos intervalos disjuntos de quem ataca seria pôr duas perguntas diferentes a
 * dividir a mesma moeda.
 *
 * ## Doze por cento, e é deliberado
 *
 * Num troca-troca de dano simples o nível 30 vale aproximadamente **12 %**: seis
 * por cento do dano recebido que deixa de acontecer, mais seis por cento que
 * volta para quem o deu. É o dobro do orçamento nominal de um socket, e é uma
 * decisão do dono - não um efeito colateral da aritmética.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não sorteia - a rolagem vem feita, do fluxo autoritativo do DEFENSOR, e quem a
 * faz é a simulação. Não conhece um alvo, uma arena, um casco, um mob nem um
 * relógio: recebe uma identidade de payload e devolve uma decisão. Não desenha,
 * e não sabe que existe um brilho branco.
 */

/* -------------------------------------------------------------------------- */
/*  A chance                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O teto de chance da Égide Espinhosa: **6 %**, nomeado uma vez.
 *
 * É o número que produz, através de {@link reflectionChanceNumerator} e do mesmo
 * denominador derivado que todas as outras loterias usam, exatamente a tabela
 * que o gate V28G.O declara:
 *
 * | nível | numerador | chance |
 * | --- | --- | --- |
 * | 1 | 600 | 0,20 % |
 * | 6 | 3 600 | 1,20 % |
 * | 15 | 9 000 | 3,00 % |
 * | 30 | 18 000 | **6,00 %**, o teto final |
 *
 * As quatro linhas saem desta única constante, sem tabela de exceções e sem
 * nenhum caso especial: `600 x nível / 300000`. A chance NUNCA é dividida antes
 * de ser comparada, pela razão de sempre - `600 x 1 / 30` são 20 basis points
 * exatos, mas `1/30` não tem representação decimal finita.
 *
 * Não há linha abaixo do 30. Um nível 31 é recusado por todas as fronteiras que
 * escrevem um perfil - base, socket, API, bilhete, instantânea - e a aritmética
 * defensiva devolve-lhe o mesmo que ao 30, porque é {@link procChanceNumerator}
 * que o garante e não uma verificação escrita aqui.
 *
 * ## É o mesmo teto do Núcleo Atômico, e a coincidência não é cópia
 *
 * As duas valem 600 bps ao máximo e o parentesco acaba aí: a Bomba paga o
 * orçamento dela em magnitude - 112,5 % do dano-base - e esta não tem magnitude
 * nenhuma para pagar, porque não inventa dano. O que ela entrega é o dano do
 * ATACANTE, virado. Duas joias com o mesmo teto e economias diferentes.
 */
export const REFLECTION_MAX_CHANCE_BPS = 600;

/**
 * O numerador desta joia, dado o seu nível somado.
 *
 * Inteiro, nunca dividido, e comparado contra {@link PROC_CHANCE_DENOMINATOR} -
 * o MESMO espaço de rolagem das outras loterias, porque a lei de chance é a
 * mesma lei. Acima de {@link PROC_LEVEL_CAP} devolve sempre o mesmo valor.
 */
export function reflectionChanceNumerator(levelSum: number): number {
  return procChanceNumerator(REFLECTION_MAX_CHANCE_BPS, levelSum);
}

/* -------------------------------------------------------------------------- */
/*  A tabela fechada: que payloads existem                                    */
/* -------------------------------------------------------------------------- */

/**
 * **Todo o efeito hostil que uma simulação pode resolver contra uma nave.**
 *
 * Uma lista fechada, e é o eixo da prova de completude: um efeito novo que possa
 * ferir alguém tem de entrar aqui, e entrar aqui obriga a preencher
 * {@link REFLECTION_POLICY} para as DUAS linhagens - o que é um erro de
 * compilação até alguém decidir. Não há default, não há fallthrough e não há
 * "os que faltam não refletem".
 *
 * Os nomes descrevem o INSTANTE de resolução e não a joia: `missile_impact` é o
 * momento em que um míssil alcançaria o casco, e nunca `jewel_missile`.
 */
export const HOSTILE_PAYLOAD_KINDS = [
  /** Um projétil de canhão a tocar o casco. */
  'cannon_shot',
  /**
   * **O contacto de uma criatura hostil que não é um jogador.**
   *
   * O mob do mundo aberto encosta-se e fere pelo simples facto de estar
   * encostado. Não vem de nenhuma joia — como `cannon_shot` — e entrou nesta
   * tabela em vez de ser encaixado noutro tipo porque não é nenhum deles: um
   * contacto de corpo não é um tiro, e não é uma serra, cuja política foi
   * decidida a pensar numa lâmina que orbita um dono.
   */
  'creature_contact',
  /** O corte do Ciclone, no acerto que o trouxe. */
  'cyclone_cut',
  /** UMA instância de dano de UMA serra orbital sobre UM casco (gate V28G.P). */
  'saw_contact',
  /** A APLICAÇÃO de um stack de veneno: a marca a nascer. */
  'poison_application',
  /** UMA parcela de um stack já aplicado. Cada uma é uma instância própria. */
  'poison_tick',
  /** A APLICAÇÃO de uma marca de sangramento. */
  'bleed_application',
  /** A detonação das marcas de um autor sobre UM casco. */
  'bleed_detonation',
  /** O instante em que um míssil alcançaria o casco, ANTES da explosão. */
  'missile_impact',
  /** O instante em que a proximidade acionaria a Bomba, ANTES da explosão. */
  'atomic_bomb_proximity',
  /** O dano em ÁREA da Bomba sobre quem não acionou a proximidade. */
  'atomic_blast_splash',
] as const;

export type HostilePayloadKind = (typeof HOSTILE_PAYLOAD_KINDS)[number];

/**
 * O tiro de canhão, nomeado uma vez.
 *
 * Existe como constante e não como literal espalhado pela mesma razão que
 * `CYCLONE_CUT_EFFECT` existe: vários sítios o escrevem - a simulação, a cena e
 * as provas - e vários literais são várias coisas que podem divergir sem que
 * nada as compare.
 *
 * ## Porque uma APRESENTAÇÃO precisa dele por nome
 *
 * Porque ele é o **único payload cujo redirecionamento não tem entidade nenhuma
 * a viajar**. O §5 do gate manda que o projétil termine no contacto, que nenhum
 * projétil de retorno nasça, e que o dano caia no atacante imediatamente -
 * portanto é o único caso em que a cena tem de desenhar a ligação entre os dois
 * cascos, porque não há objeto que a percorra.
 *
 * O Míssil e a Bomba partem MESMO da nave refletora e a cena já os desenha a
 * voar; o Ciclone, o Veneno e o Sangramento aparecem no atacante pela sua
 * própria apresentação aprovada. Nenhum dos cinco precisa de feixe.
 *
 * Isto é um facto sobre a MECÂNICA - ter ou não ter entidade - e é por isso que
 * mora aqui em vez de numa lista da cena.
 */
export const CANNON_SHOT_PAYLOAD: HostilePayloadKind = 'cannon_shot';

/**
 * **A LINHAGEM de um payload: já foi refletido, ou não.**
 *
 * ## Porque uma linhagem e não um contador
 *
 * Porque um contador permite profundidade dois. `depth < 1` é uma condição que
 * alguém escreve, que outro alguém copia com `<=`, e que uma terceira mecânica
 * incrementa no sítio errado. Uma linhagem é uma IDENTIDADE fechada: um payload
 * ou nasceu hostil, ou nasceu de uma reflexão, e não existe uma terceira coisa
 * em que ele se possa transformar.
 *
 * ## E porque a recusa está na TABELA e não numa condição
 *
 * Porque o gate exige exatamente isso. A linha `reflected` de
 * {@link REFLECTION_POLICY} recusa **todos** os nove tipos, e é construída
 * mapeando a lista fechada para uma recusa partilhada - portanto não há um par
 * `(linhagem, tipo)` que a possa autorizar, nem sequer um que alguém possa
 * esquecer de preencher. A profundidade máxima é um porque não há aresta no
 * grafo que dê a segunda, e não porque alguém contou.
 */
export const REFLECTION_LINEAGES = ['original', 'reflected'] as const;

export type ReflectionLineage = (typeof REFLECTION_LINEAGES)[number];

/**
 * O que a tabela responde sobre um par `(linhagem, tipo)`.
 *
 * Uma união e não um booleano, porque as recusas não são todas a mesma coisa e
 * a diferença é a que este gate existe para tornar legível: um splash recusa
 * porque não tem ponto de interceção, e um payload refletido recusa porque já
 * gastou a sua única reflexão. Um `false` partilhado apagaria as duas razões.
 */
export type ReflectionPolicy =
  | {
      readonly reflectable: true;
      /**
       * ONDE a interceção acontece, nomeada.
       *
       * Prosa, e deliberadamente: nenhuma máquina a lê, e não é para ler. Ela
       * existe para que a resposta a "onde é que isto é intercetado" viva ao
       * lado da decisão em vez de num documento que envelhece noutra pasta.
       */
      readonly boundary: string;
    }
  | { readonly reflectable: false; readonly reason: string };

/** A recusa de tudo o que já foi refletido. Uma só, partilhada e congelada. */
const ALREADY_REFLECTED: ReflectionPolicy = Object.freeze({
  reflectable: false,
  reason:
    'um payload refletido e todos os seus descendentes já gastaram a sua única ' +
    'reflexão: a profundidade máxima é exatamente um',
});

/**
 * **A tabela da linhagem ORIGINAL.** Uma linha por tipo, sem default.
 *
 * `Record<HostilePayloadKind, ReflectionPolicy>` é o que a torna fechada:
 * acrescentar um tipo sem decidir a sua política é um erro de compilação, e não
 * uma omissão que cai numa permissão silenciosa.
 */
const ORIGINAL_POLICY: Readonly<Record<HostilePayloadKind, ReflectionPolicy>> = Object.freeze({
  cannon_shot: Object.freeze({
    reflectable: true,
    boundary: 'o contacto do projétil com o casco, antes do dano e de qualquer proc dependente',
  }),
  /*
   * **O contacto de uma criatura: intercetável, e o dano volta para ela.**
   *
   * A fronteira é a instância de dano do contacto, exatamente como no tiro: um
   * autor, um destinatário e uma magnitude. Devolver não transfere nada — a
   * criatura não ganha joias e o refletor não ganha um corpo — e a morte que o
   * retorno causar pertence a quem refletiu, que é a regra geral da reflexão e
   * não uma regra dos mobs.
   */
  creature_contact: Object.freeze({
    reflectable: true,
    boundary: 'a instância de dano de UM contacto, antes de ela entrar no casco',
  }),
  cyclone_cut: Object.freeze({
    reflectable: true,
    boundary: 'a aplicação do corte, na sua própria fronteira e com a sua própria rolagem',
  }),
  /*
   * **A serra: o CONTACTO é intercetável, e a serra nunca muda de dono.**
   *
   * A fronteira é a instância de dano, e não a lâmina. Um contacto tem
   * exatamente o que uma reflexão precisa - um autor, um destinatário e uma
   * magnitude - e é por isso que ele entra nesta tabela como todos os outros.
   *
   * O que ela NÃO faz é o que a distingue: refletir um contacto **não transfere
   * a serra**, não a copia para o refletor e não lhe cria uma órbita. As serras
   * são do casco que as tem, pelo perfil congelado dele, e um payload
   * redirecionado é um NÚMERO a mudar de destino - nunca uma entidade a mudar
   * de dono. O refletor devolve aquele dano e volta a não ter serras nenhumas.
   *
   * E o cooldown do par serra→alvo **é consumido na mesma**: a serra tocou, a
   * instância aconteceu, e quem a levou foi outro. Não gastá-lo daria à Égide o
   * poder de fazer a serra bater mais depressa, que é o oposto do que ela é.
   */
  saw_contact: Object.freeze({
    reflectable: true,
    boundary:
      'a instância de dano de UM contacto, no par serra→alvo que a produziu; ' +
      'a lâmina não viaja e o refletor não ganha órbita nenhuma',
  }),
  poison_application: Object.freeze({
    reflectable: true,
    boundary: 'o instante em que a marca nasceria no refletor',
  }),
  poison_tick: Object.freeze({
    reflectable: true,
    boundary: 'cada parcela vencida, uma instância de cada vez',
  }),
  bleed_application: Object.freeze({
    reflectable: true,
    boundary: 'o instante em que a marca nasceria no refletor',
  }),
  bleed_detonation: Object.freeze({
    reflectable: true,
    boundary: 'a instância de dano que a ordem produz sobre ESTE casco',
  }),
  missile_impact: Object.freeze({
    reflectable: true,
    boundary: 'o instante em que o míssil alcançaria o casco, antes da explosão',
  }),
  atomic_bomb_proximity: Object.freeze({
    reflectable: true,
    boundary: 'o instante em que a proximidade acionaria a bomba, antes da explosão',
  }),
  /*
   * **A ÚNICA recusa de conteúdo desta tabela, e ela é declarada.**
   *
   * Decisão do dono no gate V28G.O. Uma explosão em área não tem ponto de
   * interceção por vítima: ela acontece num sítio, ao mesmo tempo, sobre todos
   * os cascos dentro do raio, e o payload não é uma coisa que se possa apontar
   * ao contrário para cada um deles - virar o splash de A daria um segundo
   * splash com outro centro, que é um efeito novo e não um redirecionamento.
   *
   * E a Bomba **já teve** a sua interceção: `atomic_bomb_proximity` é o instante
   * em que ela é apanhada, e um casco que a intercete recebe zero de tudo -
   * dano, área e empurrão. O que sobra aqui são os vizinhos, e para esses a
   * bomba nunca chegou a ser um payload endereçado.
   *
   * Escrito como recusa e não como ausência precisamente porque o §5 do gate
   * proíbe exceções silenciosas.
   */
  atomic_blast_splash: Object.freeze({
    reflectable: false,
    reason:
      'dano em área simultâneo sobre vários cascos: não tem ponto de interceção ' +
      'por vítima, e a Bomba já é intercetável na proximidade',
  }),
});

/**
 * A tabela da linhagem REFLETIDA: **todos os tipos, sempre recusados.**
 *
 * Construída mapeando a lista fechada para {@link ALREADY_REFLECTED}, e não
 * escrita linha a linha. Escrevê-la à mão seria criar nove sítios onde alguém
 * pode pôr `reflectable: true` num deles sem que nada compare; assim não existe
 * sequer a possibilidade sintática.
 */
const REFLECTED_POLICY: Readonly<Record<HostilePayloadKind, ReflectionPolicy>> = Object.freeze(
  Object.fromEntries(HOSTILE_PAYLOAD_KINDS.map((kind) => [kind, ALREADY_REFLECTED])) as Record<
    HostilePayloadKind,
    ReflectionPolicy
  >,
);

/** **A tabela fechada**, nas duas dimensões que a decidem. */
export const REFLECTION_POLICY: Readonly<
  Record<ReflectionLineage, Readonly<Record<HostilePayloadKind, ReflectionPolicy>>>
> = Object.freeze({
  original: ORIGINAL_POLICY,
  reflected: REFLECTED_POLICY,
});

/** Lê a tabela. A única forma de a pergunta ser feita. */
export function reflectionPolicy(
  lineage: ReflectionLineage,
  kind: HostilePayloadKind,
): ReflectionPolicy {
  return REFLECTION_POLICY[lineage][kind];
}

/**
 * Se este par pode sequer ser intercetado. **Perguntado ANTES de gastar rolagem.**
 *
 * O §4 do gate é explícito na ordem: um payload que já foi refletido "resolve
 * normalmente **sem nova rolagem**". Consumir um valor do fluxo para depois o
 * deitar fora seria fazer a sequência do defensor depender de quantos payloads
 * refletidos passaram por ele - o que é observável, porque a instantânea guarda
 * o estado do gerador.
 */
export function reflectionMayIntercept(
  lineage: ReflectionLineage,
  kind: HostilePayloadKind,
): boolean {
  return reflectionPolicy(lineage, kind).reflectable;
}

/* -------------------------------------------------------------------------- */
/*  A ponte de completude: todo o efeito de joia declara a sua política       */
/* -------------------------------------------------------------------------- */

/**
 * O que cada efeito que uma joia emite É, do ponto de vista da Reflexão.
 *
 * ## Porque esta ponte existe
 *
 * Porque {@link HOSTILE_PAYLOAD_KINDS} sozinha não obriga a nada: alguém pode
 * acrescentar um efeito novo a `JEWEL_EMITTED_EFFECTS` e nunca lhe dar um tipo
 * de payload, e a tabela acima continuaria completa. Esta é a ligação que falta
 * - `Record<JewelEmittedEffect, ...>` - e é ela que faz um efeito novo NÃO
 * COMPILAR até alguém dizer o que ele é.
 *
 * As três respostas possíveis são as três verdades que um efeito pode ter, e
 * nenhuma delas é "não sei":
 *
 * - `hostile` — fere alguém, existe neste build, e diz em que instantes é
 *   intercetável;
 * - `unbuilt` — feriria alguém, e **não tem mecânica nenhuma neste build**.
 *   Nada o emite, portanto nada o pode refletir, e o dia em que ele nascer é o
 *   dia em que esta linha tem de mudar;
 * - `not_hostile` — não fere ninguém. Uma cura não tem nada a intercetar.
 */
export type JewelEffectReflectionClass =
  | {
      readonly status: 'hostile';
      readonly payloads: readonly [HostilePayloadKind, ...HostilePayloadKind[]];
    }
  | { readonly status: 'unbuilt'; readonly note: string }
  | { readonly status: 'not_hostile'; readonly note: string };

/** Um atalho tipado, para cada linha declarar os seus tipos sem uma asserção. */
function hostile(
  ...payloads: readonly [HostilePayloadKind, ...HostilePayloadKind[]]
): JewelEffectReflectionClass {
  return Object.freeze({ status: 'hostile', payloads: Object.freeze(payloads) });
}

export const JEWEL_EFFECT_REFLECTION: Readonly<
  Record<JewelEmittedEffect, JewelEffectReflectionClass>
> = Object.freeze({
  missile: hostile('missile_impact'),
  saw_contact: hostile('saw_contact'),
  cyclone_cut: hostile('cyclone_cut'),
  atomic_blast: hostile('atomic_bomb_proximity', 'atomic_blast_splash'),
  bleed_detonation: hostile('bleed_application', 'bleed_detonation'),
  poison_tick: hostile('poison_application', 'poison_tick'),
  reflection: Object.freeze({
    status: 'not_hostile',
    note:
      'uma reflexão não é um payload próprio: ela REDIRECIONA um payload que já ' +
      'existia, e o que chega ao destinatário é o tipo original com linhagem refletida',
  }),
  lifesteal_heal: Object.freeze({
    status: 'not_hostile',
    note: 'uma cura no atacante não fere ninguém',
  }),
  vital_heal: Object.freeze({
    status: 'not_hostile',
    note: 'uma cura não fere ninguém',
  }),
});

/**
 * Os payloads hostis que **não nascem de uma joia**, declarados à parte.
 *
 * Um só, hoje: o tiro de canhão. Ele não pode aparecer em
 * {@link JEWEL_EFFECT_REFLECTION} porque não é um efeito que uma joia emita, e
 * sem esta lista a prova de completude - "todo o tipo tem de ser reclamado por
 * alguém" - não teria como o reclamar.
 *
 * Declarado e não excecionado: a prova soma as duas fontes e exige que a união
 * seja EXATAMENTE {@link HOSTILE_PAYLOAD_KINDS}, sem sobra nem falta. Um tipo
 * novo que ninguém reclame falha, e um tipo reclamado duas vezes também.
 */
export const NON_JEWEL_HOSTILE_PAYLOAD_KINDS: readonly HostilePayloadKind[] = Object.freeze([
  'cannon_shot',
  'creature_contact',
]);

/* -------------------------------------------------------------------------- */
/*  A autoria                                                                 */
/* -------------------------------------------------------------------------- */

/** O mínimo que a Reflexão precisa de saber sobre um interveniente. */
export interface ReflectionParty {
  readonly id: string;
  readonly team: string;
}

/**
 * **Se este autor pode ser alvo de uma devolução.**
 *
 * O §2 do gate em três comparações, e nenhuma delas é inferida de um valor:
 *
 * - **autor identificável** — `null` é dano ambiental, e não reflete;
 * - **não é auto-dano** — um casco não devolve a si próprio;
 * - **não é um aliado** — equipas diferentes, e a comparação é pela EQUIPA e não
 *   pelo id, porque em 3x3 dois aliados são dois ids diferentes.
 *
 * A cura não chega aqui: ela não passa por fronteira hostil nenhuma, e é a
 * tabela de {@link JEWEL_EFFECT_REFLECTION} que o declara.
 */
export function reflectionAuthorIsEligible(
  author: ReflectionParty | null,
  defender: ReflectionParty,
): boolean {
  if (author === null) {
    return false;
  }
  if (author.id === defender.id) {
    return false;
  }
  return author.team !== defender.team;
}

/* -------------------------------------------------------------------------- */
/*  A seleção                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **A rolagem defensiva.** Uma por instância hostil elegível, e no máximo uma.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo do **DEFENSOR** - um fluxo só para isto, porque partilhá-lo com a
 * loteria ofensiva, com o veneno ou com o sangramento deslocaria a sequência de
 * todos eles.
 *
 * O intervalo é `[0, w)` e não tem vizinhos: este sorteio tem um só ocupante,
 * como o do veneno e o do sangramento têm.
 *
 * Devolve `false` quando a tabela recusa o par, quando a nave não tem a joia, e
 * quando a rolagem cai fora. As três razões são diferentes e nenhuma produz
 * reflexão - e a primeira é a única que o chamador tem de distinguir, porque é
 * ela que decide se sequer se gasta uma rolagem (ver
 * {@link reflectionMayIntercept}).
 */
export function selectReflection(
  profile: JewelSocketProfile,
  lineage: ReflectionLineage,
  kind: HostilePayloadKind,
  roll: number,
): boolean {
  if (!reflectionMayIntercept(lineage, kind)) {
    return false;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`reflection roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  const owned = profile.entries.find((entry) => entry.id === DAMAGE_REFLECTION_JEWEL_ID);
  if (!owned) {
    return false;
  }
  const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
  const width = reflectionChanceNumerator(Number(levelSum));
  return width > 0 && roll < width;
}

/* -------------------------------------------------------------------------- */
/*  O fluxo                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * O rótulo do fluxo de REFLEXÃO, versionado como todos os outros.
 *
 * ## Porque um fluxo próprio, por participante e por finalidade
 *
 * Porque a razão de sempre vale aqui com mais força: esta rolagem acontece do
 * lado do DEFENSOR e a cadência dela é ditada por quantos payloads o adversário
 * manda. Consumi-la de um fluxo partilhado faria a sequência de runas, spawns,
 * procs, veneno e sangramento de um jogador depender de quantas vezes ele foi
 * atingido - e uma sala com a mesma semente deixaria de ser a mesma sala por
 * causa de uma joia que ele nem tem equipada.
 *
 * ## E porque é consumido CONDICIONALMENTE
 *
 * Porque serve uma finalidade só e mais ninguém o lê, como o do veneno e o do
 * sangramento. A regra de consumir sempre existe para fluxos cuja sequência
 * OUTRA coisa observa; aqui a única coisa que observa a sequência é a próxima
 * instância hostil elegível contra o mesmo casco, e a instantânea guarda o
 * estado do gerador.
 *
 * Não há gémeo de caça: o gate V28G.O é só de PvP por decisão do dono, e um
 * rótulo declarado sem nada que o consuma seria uma promessa que ninguém cumpre.
 * A caça tem exatamente uma fonte de dano à nave - um mob que atravessa a
 * fronteira e é removido no mesmo tique - e a decisão de o que fazer com ela é
 * a D-4.4, que continua pendente.
 */
export const PVP_REFLECTION_RNG_LABEL = 'pvp-jewel-reflection-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_REFLECTION_RNG_LABEL = 'open-world-jewel-reflection-v1';
